/**
 * NexaStayLayout – all layout and context for the NexaStay hotel website (public booking engine).
 *
 * This file owns:
 * - Hotel loading and NexaStayThemeProvider (theme presets + accent from hotel)
 * - Body class isolation (body.className = 'app nexastay') so main-app global CSS does not apply
 * - NexaStayHeader, main content (Outlet), NexaStayFooter
 * - NexaStay.css scoped under .nexastay-root
 * - MUI ThemeProvider so DatePicker, icons, and all MUI components use light/dark per NexaStay preset
 *
 * No NexaStay hotel-website logic lives in MainLayout; it stays here.
 */
import React, { useState, useEffect, useMemo, Suspense } from 'react';
import { Outlet, useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import NexaStayHeader from './NexaStayHeader';
import NexaStayFooter from './NexaStayFooter';
import { getHotelInfo } from './nexastayApi';
import { getGuestStorageKey } from './nexastayApi';
import { NexaStayThemeProvider, useNexaStayThemeContext } from './NexaStayThemeContext';
import { resolveNexaStayHotelId } from './useNexaStayBasePath';
import { buildDialogThemeOverrides } from '../shared/theme/dialogOverrides';
import './NexaStay.css';

function NexaStayLayoutInner() {
  const { theme } = useNexaStayThemeContext();
  const bgSx = { minHeight: '100vh', bgcolor: theme.bg };

  const muiTheme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: theme.mode,
          primary: { main: theme.accent },
          ...(theme.mode === 'dark'
            ? {
                background: { default: theme.bg, paper: theme.paper },
                text: { primary: theme.textPrimary, secondary: theme.textSecondary },
              }
            : {
                background: { default: theme.bg, paper: theme.paper },
                text: { primary: theme.textPrimary, secondary: theme.textSecondary },
              }),
        },
        components: {
          MuiOutlinedInput: {
            styleOverrides: {
              root: {
                '& fieldset': { borderColor: theme.borderAccent },
                '&:hover fieldset': { borderColor: theme.accent },
                '&.Mui-focused fieldset': { borderColor: theme.accent },
              },
            },
          },
          MuiInputLabel: {
            styleOverrides: {
              root: {
                '&.Mui-focused': { color: theme.accent },
              },
            },
          },
          MuiDrawer: {
            defaultProps: {
              SlideProps: { appear: true },
              transitionDuration: { enter: 350, exit: 250 },
              ModalProps: { keepMounted: true },
            },
            styleOverrides: {
              paperAnchorRight: ({ theme }) => ({
                maxWidth: '100vw',
                boxSizing: 'border-box',
                width: '100vw',
                [theme.breakpoints.up('sm')]: {
                  width: 'min(640px, 100vw)',
                },
                [theme.breakpoints.up('md')]: {
                  width: 'min(720px, 100vw)',
                },
              }),
            },
          },
          ...buildDialogThemeOverrides({ accentColor: theme.accent }),
        },
      }),
    [theme.mode, theme.accent, theme.bg, theme.paper, theme.textPrimary, theme.textSecondary, theme.borderAccent]
  );

  return (
    <ThemeProvider theme={muiTheme}>
      <Box
        className="nexastay-root"
        sx={{
          ...bgSx,
          color: theme.textPrimary,
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          overflowX: 'hidden',
        }}
      >
        <NexaStayHeader />
        <Box
          component="main"
          sx={{
            flex: 1,
            paddingTop: { xs: 'calc(56px + 12px + env(safe-area-inset-top, 0))', sm: 'calc(64px + 16px + env(safe-area-inset-top, 0))' },
            pb: 0,
            paddingBottom: 'env(safe-area-inset-bottom, 0)',
            minWidth: 0,
          }}
        >
          <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 280 }}><CircularProgress /></Box>}>
            <Outlet />
          </Suspense>
        </Box>
        <NexaStayFooter />
      </Box>
    </ThemeProvider>
  );
}

export default function NexaStayLayout() {
  const { hotelId: routeHotelId } = useParams();
  const hotelId = resolveNexaStayHotelId(routeHotelId);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [hotel, setHotel] = useState(null);

  useEffect(() => {
    if (!hotelId) return;
    let cancelled = false;
    getHotelInfo(hotelId)
      .then((data) => { if (!cancelled) setHotel(data); })
      .catch(() => { if (!cancelled) setHotel(null); });
    return () => { cancelled = true; };
  }, [hotelId]);

  // Record booking engine visit once per session (unique visitor = 1 per IP per period in backend)
  useEffect(() => {
    if (!hotelId || !hotel) return;
    const key = `nexastay_visit_${hotelId}`;
    try {
      if (sessionStorage.getItem(key)) return;
    } catch (_) {}
    fetch(`/nexastay/api/hotel/${encodeURIComponent(hotelId)}/visit/`, {
      method: 'GET',
      credentials: 'same-origin',
    })
      .then(() => {
        try { sessionStorage.setItem(key, '1'); } catch (_) {}
      })
      .catch(() => {});
  }, [hotelId, hotel]);

  // After Google OAuth redirect: read guest_id from URL, store in localStorage, then clean URL
  useEffect(() => {
    const guestId = searchParams.get('guest_id');
    if (hotelId && guestId) {
      try {
        localStorage.setItem(getGuestStorageKey(hotelId), String(guestId));
      } catch (_) {}
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('guest_id');
      nextParams.delete('error');
      const search = nextParams.toString();
      navigate(window.location.pathname + (search ? `?${search}` : ''), { replace: true });
      return;
    }
    // Strip error param (e.g. google_not_configured) so URL is clean
    const errorParam = searchParams.get('error');
    if (errorParam && hotelId) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('error');
      const search = nextParams.toString();
      navigate(window.location.pathname + (search ? `?${search}` : ''), { replace: true });
    }
  }, [hotelId, searchParams, navigate]);

  // Isolate NexaStay from main app: set body class so main-app global CSS does not apply here
  useEffect(() => {
    const prev = document.body.className;
    document.body.className = 'app nexastay';
    return () => { document.body.className = prev || 'app'; };
  }, []);

  return (
    <NexaStayThemeProvider hotel={hotel}>
      <NexaStayLayoutInner />
    </NexaStayThemeProvider>
  );
}
